<template>
  <div class="plan-grid-wrap">
    <gc-filter-split-view
      :showLeftContainer="showLeftContainer"
      :showRightContainer="showRightContainer"
      :rightContainerWidth="250"
    >
      <template #leftContent>
        <PlanToolbar
          :current-date="anchorDate"
          :view-mode="viewMode"
          @navigate="navigate"
          @set-view="setViewMode"
          @date-selected="onDateSelected"
          @go-today="goToday"
          :showRightContainer="showRightContainer"
          @set-show-right-container="onSetShowRightContainer"
          @print="onPrint"
        />

        <PlanPrintView
          ref="planPrintView"
          :branch-label="branchLabel"
          :view-mode="viewMode"
          :columns="columns"
          :processes="visibleProcesses"
          :get-cell-tiles="getCellTiles"
          :work-order-line-counts="workOrderLineCounts"
        />

        <div v-if="loading" class="plan-loading" :style="{ height: maxHeight }">
          Loading…
        </div>

        <div
          v-else
          class="plan-grid-scroll"
          :style="{
            minHeight: maxHeight ? parseInt(maxHeight) + 28 + 'px' : 'auto',
            maxHeight: maxHeight ? parseInt(maxHeight) + 28 + 'px' : 'auto',
            overflowY: maxHeight ? 'auto' : 'visible',
          }"
        >
          <div class="plan-grid" :style="gridTemplateStyle">
            <div class="plan-corner plan-sticky-tl">Process</div>

            <div
              v-for="col in columns"
              :key="col.key"
              class="plan-col-hdr plan-sticky-top"
            >
              <div class="plan-col-day">{{ col.label }}</div>
              <div class="plan-col-date">{{ col.subLabel }}</div>
            </div>

            <template v-for="proc in visibleProcesses">
              <div
                :key="'rh-' + proc.ProcessCode"
                class="plan-row-hdr plan-sticky-left"
              >
                <div class="plan-proc-code">{{ proc.Description }}</div>
                <div class="plan-proc-name">{{ proc.ProcessCode }}</div>
              </div>

              <div
                v-for="col in columns"
                :key="proc.ProcessCode + '-' + col.key"
                class="plan-cell"
              >
                <div class="plan-cell-inner">
                  <PlanTile
                    v-for="tile in getCellTiles(proc.ProcessCode, col.key)"
                    :key="tile.RecordId"
                    :tile="tile"
                    :line-count="
                      workOrderLineCounts[tile.WorkOrderCode + '|' + tile.ProcessCode] || 1
                    "
                    @open-modal="onTileModal(tile)"
                  />
                </div>
              </div>
            </template>
          </div>
        </div>
      </template>

      <template #rightContent>
        <gc-filter-panel
          :toolbarVisible="true"
          :toolbar="filterToolbar"
          :formItemData="filterFormItems"
          :setGridPanelFilter="setGridPanelFilter"
          :gridFoolter="false"
          :showLoading="showLoading"
          :from="'transaction-screen'"
          :isNormal="false"
          :persistenceKey="undefined"
          :overWriteClear="true"
          :ref="'gc-filter-panel'"
          :heightAdjustment="36"
        >
        </gc-filter-panel>
      </template>
    </gc-filter-split-view>
  </div>
</template>

<script>
import { ProductionCalendarService } from "@/_services/api/production/production-calendar.service";
import PlanTile from "./plan-tile.vue";
import PlanToolbar from "./plan-toolbar.vue";
import PlanPrintView from "./plan-print-view.vue";
import { getCalendarFilterValues } from "../../../_helpers/calendar/filter-panel.js";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export default {
  name: "ProductionSchedulePlan",

  components: { PlanTile, PlanToolbar, PlanPrintView },

  emits: ["open-wo-so-modal", "open-maintenance-modal"],

  props: {
    branchCode: { type: String, required: true },
    branchLabel: { type: String, default: "" },
    processes: { type: Array, default: () => [] },
    selectedProcesses: { type: Array, default: () => [] },
    statusFilter: { type: Array, default: () => [] },
    showLoading: { type: Function, default: () => {} },
    maxHeight: { type: String, default: null },
    filterItems: { type: Array, default: () => [] },
  },

  data() {
    return {
      viewMode: "week",
      anchorDate: new Date(),
      tiles: [],
      loading: false,

      showLeftContainer: true,
      showRightContainer: true,

      filterToolbar: {
        close: {
          icon: "showpanel",
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
            this.$nextTick(() => {});
          },
        },
      },
      filterValues: undefined,
    };
  },

  computed: {
    /**
     * Retun filter panel form items
     */
    filterFormItems() {
      return this.filterItems.filter((filter) => filter.visible);
    },
    normalizedProcesses() {
      return this.processes.map((proc) => ({
        ProcessCode: proc.code,
        Description: proc.text,
      }));
    },

    // Extracts every `ProcessCode eq 'X'` value out of the raw filterValues OData
    // string (set by setGridPanelFilter), regardless of how many other field
    // clauses it's ANDed/ORed together with.
    filterProcessCodes() {
      if (!this.filterValues) return [];
      const matches = this.filterValues.match(/ProcessCode eq '[^']+'/g) || [];
      return matches.map((clause) => clause.match(/'([^']+)'/)[1]);
    },

    visibleProcesses() {
      let list = this.normalizedProcesses;

      if (this.selectedProcesses.length > 0) {
        list = list.filter((proc) =>
          this.selectedProcesses.includes(proc.ProcessCode),
        );
      }

      if (this.filterProcessCodes.length > 0) {
        list = list.filter((proc) =>
          this.filterProcessCodes.includes(proc.ProcessCode),
        );
      }

      return list;
    },

    columns() {
      const toKey = (dt) =>
        `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(
          2,
          "0",
        )}-${String(dt.getDate()).padStart(2, "0")}`;

      const mondayOf = (dt) => {
        const r = new Date(dt);
        const day = r.getDay();
        r.setDate(r.getDate() - (day === 0 ? 6 : day - 1));
        return r;
      };

      const buildDay = (dt) => ({
        key: toKey(dt),
        label: DAY_NAMES[dt.getDay()],
        subLabel: `${dt.getDate()} ${MONTH_NAMES[dt.getMonth()]}`,
        dateObj: dt,
      });

      if (this.viewMode === "day") {
        return [buildDay(new Date(this.anchorDate))];
      }

      const mon = mondayOf(this.anchorDate);
      const span = this.viewMode === "workweek" ? 5 : 6;

      return Array.from({ length: span }, (_, i) => {
        const dt = new Date(mon);
        dt.setDate(mon.getDate() + i);
        return buildDay(dt);
      });
    },

    gridTemplateStyle() {
      return {
        gridTemplateColumns: `160px repeat(${this.columns.length}, minmax(140px, 1fr))`,
      };
    },

    groupedTiles() {
      // Grid column is keyed off StartDate (earliest shift allocation start
      // date per WorkOrderCode+ProcessCode), not PackDate — Task 73071. A WO
      // spanning multiple days is placed on its start date only.
      const grid = {};
      this.tiles.forEach((tile) => {
        if (!tile.StartDate) return;
        const dateKey = this.dateKeyOf(tile.StartDate);
        if (!grid[tile.ProcessCode]) grid[tile.ProcessCode] = {};
        if (!grid[tile.ProcessCode][dateKey])
          grid[tile.ProcessCode][dateKey] = [];
        grid[tile.ProcessCode][dateKey].push(tile);
      });
      return grid;
    },

    // Number of linked SO lines per Work Order, scoped to Process (Task
    // 73070) — the alpha suffix only displays once a WO has 2+ lines within
    // the same process row. Keyed by WorkOrderCode+ProcessCode rather than
    // WorkOrderCode alone, since a WO can appear against more than one
    // process. Counted across all currently-fetched tiles, not just within
    // one grid cell.
    workOrderLineCounts() {
      const counts = {};
      this.tiles.forEach((tile) => {
        const key = `${tile.WorkOrderCode}|${tile.ProcessCode}`;
        counts[key] = (counts[key] || 0) + 1;
      });
      return counts;
    },
  },

  watch: {
    branchCode() {
      this.fetchTiles();
    },
    selectedProcesses() {
      this.fetchTiles();
    },
    statusFilter() {
      this.fetchTiles();
    },
    viewMode() {
      this.fetchTiles();
    },
    anchorDate() {
      this.fetchTiles();
    },
  },

  mounted() {
    this.fetchTiles();
  },

  methods: {
    navigate(direction) {
      // Columns always floor anchorDate to the Monday of its week (see `columns`),
      // so the step must be a full 7-day week to reliably land on the next/previous
      // Monday-anchored period — stepping by the column count (5/6) can drift back
      // into the same displayed week once flooring is applied.
      const step = this.viewMode === "day" ? 1 : 7;
      const next = new Date(this.anchorDate);
      next.setDate(next.getDate() + direction * step);
      this.anchorDate = next;
    },

    setViewMode(mode) {
      this.viewMode = mode;
    },

    onDateSelected(date) {
      this.anchorDate = date;
    },

    goToday() {
      this.anchorDate = new Date();
    },

    dateKeyOf(value) {
      const d = value instanceof Date ? value : new Date(value);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(d.getDate()).padStart(2, "0")}`;
    },

    getCellTiles(processCode, colKey) {
      return (
        (this.groupedTiles[processCode] &&
          this.groupedTiles[processCode][colKey]) ||
        []
      );
    },

    async fetchTiles() {
      if (!this.branchCode || this.columns.length === 0) return;

      this.loading = true;
      this.showLoading(true);

      try {
        const startDate = this.columns[0].dateObj.toFilterDateString();
        // Exclusive upper bound (midnight of the day after the last visible column) so
        // records on the last day with a non-midnight StartDate time aren't cut off.
        const lastDay = this.columns[this.columns.length - 1].dateObj;
        const endDateExclusive = new Date(lastDay);
        endDateExclusive.setDate(endDateExclusive.getDate() + 1);

        const res = await ProductionCalendarService.getPlanTiles(
          this.branchCode,
          this.selectedProcesses,
          startDate,
          endDateExclusive.toFilterDateString(),
          this.filterValues,
        );

        this.tiles = (res && res.data && res.data.value) || [];

        this.tiles.forEach((tile) => {
          if (tile.StartDate) {
            tile.StartDate = new Date(tile.StartDate.replace("Z", ""));
          }
        });
      } catch (err) {
        this.tiles = [];
      } finally {
        this.loading = false;
        this.showLoading(false);
      }
    },

    // Filter panel methods

    /**
     * Filter Panel values
     * @param e
     * @param valueChanged
     */
    setGridPanelFilter(e, valueChanged, filterType = "Apply") {
      if (valueChanged && filterType.toLowerCase() === "refresh") {
        this.fetchTiles();
      } else {
        this.filterValues = getCalendarFilterValues(e, valueChanged);
        if (valueChanged) {
          this.fetchTiles();
        }
      }
    },

    onSetShowRightContainer(value) {
      this.showRightContainer = value;
    },

    onPrint() {
      this.$refs.planPrintView.open();
    },

    // Routes a tile click to the right existing modal-opening function on
    // production-calendar.vue, keyed off WOType (SO/MTS vs Maintenance).
    onTileModal(tile) {
      if (tile.WOType === "Maintenance") {
        this.$emit("open-maintenance-modal", tile);
      } else {
        this.$emit("open-wo-so-modal", tile);
      }
    },
  },
};
</script>

<style scoped lang="scss">
.plan-grid-wrap {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
  position: relative;
  background: #fff;

  .plan-loading {
    padding: 40px;
    color: #9ca3af;
    font-size: 13px;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .plan-grid-scroll {
    flex: 1;
    overflow: auto;
  }

  .plan-grid {
    display: grid;
    min-width: 100%;
  }

  .plan-sticky-tl {
    position: sticky;
    top: 0;
    left: 0;
    z-index: 4;
  }
  .plan-sticky-top {
    position: sticky !important;
    top: 0;
    z-index: 3;
  }
  .plan-sticky-left {
    position: sticky;
    left: 0;
    z-index: 2;
  }

  .plan-corner {
    background: #f9fafb;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 8px 12px;
    font-size: 11px;
    font-weight: 500;
    color: #9ca3af;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .plan-col-hdr {
    background: #f9fafb;
    border-right: 1px solid #e5e7eb;
    border-bottom: 2px solid #e5e7eb;
    padding: 6px 10px;
    text-align: center;
  }
  .plan-col-day {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
  }
  .plan-col-date {
    font-size: 10px;
    color: #9ca3af;
    margin-top: 1px;
  }

  .plan-row-hdr {
    background: #fff;
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 8px 12px;
  }
  .plan-proc-code {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
  }
  .plan-proc-name {
    font-size: 10px;
    color: #9ca3af;
    margin-top: 2px;
  }

  .plan-cell {
    border-right: 1px solid #e5e7eb;
    border-bottom: 1px solid #e5e7eb;
    padding: 5px;
    background: #fff;
  }
  .plan-cell-inner {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
}
</style>
